import _ from 'lodash'
import { Instance, SnapshotOut, types } from 'mobx-state-tree'
import {
  MembershipTypeModel,
  MembershipTypeModelType,
  membershipTypeModelPrimitives,
  MembershipModelType,
  MembershipModel,
  GetBillingDetailsModel,
  GetBillingDetailsModelType,
  MembershipTypes
} from '../api-store'
import { GetPromotionCodeIdByCodeResponseModel, GetPromotionCodeIdByCodeResponseModelType } from '../api-store'
import { withRootStore } from '../extensions'

import { extendLogger, LoggerContexts } from '../../utils/logger'
import { ApplePaymentSignatureInput, InputCancellationSurvey } from '../api-store/RootStore.base'

const logger = extendLogger(LoggerContexts.Membership)

export const MembershipStore = types
  .model('MembershipStore')
  .props({
    membershipTypes: types.optional(types.array(MembershipTypeModel), []),
    promotionalCode: types.optional(types.maybe(GetPromotionCodeIdByCodeResponseModel), undefined),
    subscription: types.optional(types.maybeNull(MembershipModel), undefined),
    newSubscription: types.optional(types.boolean, false),
    billingDetails: types.optional(types.maybeNull(GetBillingDetailsModel), undefined),
    justSubscribed: types.optional(types.boolean, false)
  })
  .extend(withRootStore)
  .views((self) => ({
    get isSubscribed() {
      return !!self.subscription && (self.subscription?.status === 'active' || self.subscription?.status === 'past_due')
    },
    get isApple() {
      return !!self.subscription && self.subscription?.stripeSubscriptionId === 'apple-placeholder'
    },
    get isCanceled() {
      return !!self.subscription && self.subscription?.status === 'canceled'
    },
    get noSubscriptionOrCanceledOne() {
      return !self.subscription || self.subscription?.status === 'canceled'
    }
  }))

  .actions((self) => ({
    setMembershipType(membershipTypes: MembershipTypeModelType[]) {
      self.membershipTypes.replace(membershipTypes)
    },
    setPromotionalCode(promotionalCode: GetPromotionCodeIdByCodeResponseModelType | null) {
      const clonedPromotoionalCode = _.cloneDeep(promotionalCode)
      self.promotionalCode = GetPromotionCodeIdByCodeResponseModel.create({ ...clonedPromotoionalCode })
    },
    setMySubscription(mySubscription: MembershipModelType | null) {
      self.subscription = mySubscription
    },
    setNewSubscription(newSubscription: boolean) {
      self.newSubscription = newSubscription
    },
    setBillingDetails(billingDetails: GetBillingDetailsModelType | null) {
      self.billingDetails = billingDetails
    },
    setJustSubscribed(justSubscribed: boolean) {
      self.justSubscribed = justSubscribed
    }
  }))
  .actions((self) => ({
    async getMembershipType(): Promise<void> {
      try {
        const result = await self.rootStore.api.queryGetMemberships(
          undefined,
          membershipTypeModelPrimitives.toString(),
          {
            fetchPolicy: 'no-cache'
          }
        )
        self.setMembershipType(result?.getMemberships || [])
      } catch (e) {
        console.error('catch getMembershipType', e)
        logger.error('queryGetMemberships API Error: ', e)
        throw e
      }
    },
    async getPromotionByPromoCode({ code }: { code: string }): Promise<void> {
      try {
        const { getPromotionCodeIdByCode } = await self.rootStore.api.queryGetPromotionCodeIdByCode(
          { code },
          undefined,
          {
            fetchPolicy: 'no-cache'
          }
        )
        self.setPromotionalCode(getPromotionCodeIdByCode)
      } catch (e) {
        self.setPromotionalCode(null)
        logger.error('queryGetPromotionCodeIdByCode API Error: ', e)
      }
    },

    async getMySubscription(): Promise<void> {
      try {
        const { getMySubscription } = await self.rootStore.api.queryGetMySubscription(undefined, undefined, {
          fetchPolicy: 'no-cache'
        })
        self.setMySubscription(getMySubscription)
      } catch (e) {
        self.setMySubscription(null)
        logger.error('queryGetMySubscription API Error: ', e)
      }
    },
    async getBillingDetails(): Promise<void> {
      try {
        const { getBillingDetails } = await self.rootStore.api.queryGetBillingDetails(undefined, undefined, {
          fetchPolicy: 'no-cache'
        })
        self.setBillingDetails(getBillingDetails)
      } catch (e) {
        self.setBillingDetails(null)
        logger.error('queryGetMySubscription API Error: ', e)
      }
    },
    activeNewSubscriptionState() {
      self.setNewSubscription(true)
    },
    clearNewSubscriptionState() {
      self.setNewSubscription(false)
    },
    async cancelMembership(id: string, inputCancellationSurvey: InputCancellationSurvey) {
      try {
        await self.rootStore.api.mutateCancelUserSubscription({ stripeSubscriptionId: id })
        await self.rootStore.api.mutateCancellationSurvey({ inputCancellationSurvey })
      } catch (e) {
        self.setBillingDetails(null)
        logger.error('queryCancelMembership API Error: ', e)
      }
    },
    async keepUserSubscription(id: string) {
      try {
        const { keepUserSubscription } = await self.rootStore.api.mutateKeepUserSubscription({
          stripeSubscriptionId: id
        })
        return keepUserSubscription
      } catch (e) {
        logger.error('queryKeepUserSubscription API Error: ', e)
      }
    },
    async createAppleSubscription() {
      try {
        const response = await self.rootStore.api.mutateCreateAppleSubscription()
        if (response.createAppleSubscription) {
          self.setMySubscription(response.createAppleSubscription)
        }
      } catch (e) {
        logger.error('createAppleSubscription error', e)
      }
    },
    async createApplePaymentSignature(paymentParams: ApplePaymentSignatureInput) {
      try {
        const response = await self.rootStore.api.mutateCreateApplePaymentSignature({
          paymentParams
        })
        return response.createApplePaymentSignature
      } catch (e) {
        logger.error('createApplePaymentSignature error', e)
      }
    },
    async updateUserSubscription({ changeTo }: { changeTo: MembershipTypes }) {
      try {
        const response = await self.rootStore.api.mutateChangeUserSubscription({ changeTo })
        if (response) {
          const { getMySubscription } = await self.rootStore.api.queryGetMySubscription(undefined, undefined, {
            fetchPolicy: 'no-cache'
          })
          self.setMySubscription(getMySubscription)
        }
      } catch (e) {
        logger.error('updateUserSubscription error', e)
        return false
      }
    }
  }))

/**
 * The MembershipStore instance.
 */
export type MembershipStoreType = Instance<typeof MembershipStore>

/**
 * The data of a MembershipStore.
 */
export type MembershipStoreSnapshot = SnapshotOut<typeof MembershipStore>
